import { useRef, useState, type ComponentType, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  FormControl as MuiFormControl,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  commentsRequired,
  presentationOf,
  problemsIn,
  withBannersShown,
  type BannerPlacement,
  type FormContext,
  type FormElement,
  type FormProblem,
  type FormControl as ItemControl,
  type JsonValue,
  type MeaningSourceUnbranded,
  type PresentationUnbranded,
  type PublishedInstrumentSchema,
  type SubmissionDocumentUnbranded,
} from "@thalamos/haven-common";

import {
  answered,
  answerOf,
  commentProblemsAt,
  hiddenIn,
  problemsAt,
  radioValueOf,
  withoutHidden,
  withRequiredGroups,
  type ItemElement,
  type QuestionAnswer,
} from "./answering.js";
import { ChoiceOptionLabel, Description } from "./ChoiceOptionLabel.js";

export type AssessmentFormProps = {
  /** The plain or decorated form, in the order it presents its elements. */
  form: readonly FormElement[];
  /** The stored schema the form was built from, which a submission must satisfy. */
  schema: PublishedInstrumentSchema;
  /** The revision `schema` is, whose meanings the form's canonical texts are. */
  source: MeaningSourceUnbranded;
  /** The language the form was built in, and the one its own words are shown in. */
  language: string;
  context: FormContext;
  /** Answers to carry on from, such as a saved draft; the form starts empty without them. */
  initialDocument?: SubmissionDocumentUnbranded;
  /** Called only with a document the form accepts, and the evidence of what it showed. */
  onSubmit: (submission: {
    document: SubmissionDocumentUnbranded;
    presentation: PresentationUnbranded;
  }) => Promisable<void>;
  /** Called with the document after every answer, for a caller keeping a draft. */
  onDocumentChange?: (
    document: SubmissionDocumentUnbranded,
  ) => Promisable<void>;
  /** While true, a submission is on its way and another cannot be made. */
  submitting?: boolean;
  /** Shown beneath the submit button, such as whether the draft is saved. */
  footer?: ReactNode;
  /**
   * Whether to show beside the form what it would send, whether it would
   * accept it, and the evidence of what it showed: a working view for those
   * exploring the form, not for a respondent. Shown unless turned off.
   */
  showWorking?: boolean;
  /** Shown at the top of the working view, above what would be sent; hidden with it. */
  aside?: ReactNode;
};

/**
 * One wording for an item's comment box, so evidence names the label shown and
 * the language i18next actually used, which differs from `language` where the
 * locale falls back.
 */
const commentLabelFor =
  (t: TFunction, language: string) => (element: ItemElement) => {
    const { res, usedLng } = t("assessment.form.commentOn", {
      title: element.presentation.title.text,
      lng: language,
      returnDetails: true,
    });

    return { text: res, language: usedLng };
  };

export type ControlProps<Control extends ItemControl> = {
  control: Control;
  id: string;
  /** The id of the element holding the item's question, which names the control. */
  labelledBy: string;
  required: boolean;
  value: JsonValue | undefined;
  error: string | undefined;
  /** `undefined` is no answer at all. */
  onChange: (value: JsonValue | undefined) => void;
};

const ChoiceControl = ({
  control,
  id,
  labelledBy,
  required,
  value,
  error,
  onChange,
}: ControlProps<Extract<ItemControl, { kind: "choice" }>>) => (
  <MuiFormControl id={id} error={error !== undefined} required={required}>
    <RadioGroup
      aria-labelledby={labelledBy}
      value={value === undefined ? "" : radioValueOf(value)}
      onChange={(_event, chosen) => {
        onChange(
          control.options.find(
            (option) => radioValueOf(option.value) === chosen,
          )?.value,
        );
      }}
    >
      {control.options.map((option) => (
        <FormControlLabel
          key={radioValueOf(option.value)}
          value={radioValueOf(option.value)}
          control={<Radio />}
          label={<ChoiceOptionLabel option={option} />}
        />
      ))}
    </RadioGroup>
    {error === undefined ? null : <FormHelperText>{error}</FormHelperText>}
  </MuiFormControl>
);

const TextControl = ({
  id,
  labelledBy,
  required,
  value,
  error,
  onChange,
  type,
}: ControlProps<ItemControl> & { type?: "date" }) => (
  <TextField
    id={id}
    required={required}
    type={type}
    value={typeof value === "string" ? value : ""}
    error={error !== undefined}
    helperText={error}
    onChange={(event) => {
      onChange(event.target.value === "" ? undefined : event.target.value);
    }}
    slotProps={{ htmlInput: { "aria-labelledby": labelledBy } }}
    fullWidth
  />
);

const DateControl = (props: ControlProps<ItemControl>) => (
  <TextControl {...props} type="date" />
);

/**
 * How each kind of control is drawn. Keyed by every kind, so a kind added to
 * the form model without a way to draw it does not compile.
 */
const controls: {
  readonly [Kind in ItemControl["kind"]]: ComponentType<
    ControlProps<Extract<ItemControl, { kind: Kind }>>
  >;
} = {
  choice: ChoiceControl,
  text: TextControl,
  date: DateControl,
};

/** The control an item's kind of answer is drawn with. */
export const ControlFor = (props: ControlProps<ItemControl>) => {
  // The component found is always the one for this control's kind; the
  // compiler cannot correlate the lookup with the union it came from.
  const Control = controls[props.control.kind] as ComponentType<
    ControlProps<ItemControl>
  >;

  return <Control {...props} />;
};

const ItemField = ({
  element,
  commentLabel,
  commentRequired,
  answer,
  problems,
  commentProblems,
  onAnswer,
}: {
  element: ItemElement;
  commentLabel: string;
  /** Whether the form requires a comment, given the answers so far. */
  commentRequired: boolean;
  answer: QuestionAnswer | undefined;
  problems: readonly string[];
  commentProblems: readonly string[];
  onAnswer: (answer: QuestionAnswer | undefined) => void;
}) => {
  const id = `item-${element.at.group}-${element.at.item}`;
  const questionId = `${id}-question`;

  const { banners } = element.presentation;
  const bannersAt = (placement: BannerPlacement) =>
    banners
      .filter((banner) => banner.placement === placement)
      .map((banner, index) => (
        <Alert key={`${placement}-${String(index)}`} severity={banner.severity}>
          {banner.text.text}
        </Alert>
      ));
  // Keyed by every `BannerPlacement`, so adding one fails to compile until it
  // has an entry here, which the JSX below must then draw.
  const placed: Readonly<Record<BannerPlacement, ReactNode>> = {
    before_question: bannersAt("before_question"),
    after_question: bannersAt("after_question"),
    before_answer: bannersAt("before_answer"),
    after_answer: bannersAt("after_answer"),
    before_comment: bannersAt("before_comment"),
    after_comment: bannersAt("after_comment"),
  };

  return (
    <Stack spacing={1}>
      {placed.before_question}
      {/* The question stands apart from its control, and is not a <label>,
          so it can hold richer content than a control's label could. */}
      <FormLabel
        id={questionId}
        component="div"
        required={element.asking.required !== "no"}
        error={problems.length > 0}
      >
        {element.presentation.title.text}
        <Description text={element.presentation.description?.text} />
      </FormLabel>
      {placed.after_question}
      {placed.before_answer}
      <ControlFor
        control={element.presentation.control}
        id={id}
        labelledBy={questionId}
        required={element.asking.required !== "no"}
        value={answer?.response}
        error={problems.length === 0 ? undefined : problems.join(" ")}
        // A comment says something about the response, so clearing the
        // response clears the comment with it.
        onChange={(response) => {
          onAnswer(
            response === undefined ? undefined : { ...answer, response },
          );
        }}
      />
      {placed.after_answer}
      {placed.before_comment}
      {element.presentation.acceptsComment ? (
        <TextField
          label={commentLabel}
          value={answer?.comment ?? ""}
          disabled={answer === undefined}
          required={commentRequired}
          error={commentProblems.length > 0}
          helperText={
            commentProblems.length === 0 ? undefined : commentProblems.join(" ")
          }
          onChange={(event) => {
            if (answer !== undefined) {
              const { value } = event.target;

              onAnswer(
                value === ""
                  ? { response: answer.response }
                  : { ...answer, comment: value },
              );
            }
          }}
          size="small"
          multiline
        />
      ) : null}
      {placed.after_comment}
    </Stack>
  );
};

/**
 * An instrument's form, filled in as a document of the shape a submission
 * takes, with what it would send, the evidence of what it showed, and whether
 * it would accept it shown beside unless `showWorking` is off.
 *
 * Hidden items disappear as they hide, and their answers go with them, as
 * does the heading of a group asking none of its items.
 */
export const AssessmentForm = ({
  form,
  schema,
  source,
  language,
  context,
  initialDocument = {},
  onSubmit,
  onDocumentChange,
  submitting = false,
  footer,
  showWorking = true,
  aside,
}: AssessmentFormProps) => {
  const { t } = useTranslation();
  const [document, setDocument] = useState<SubmissionDocumentUnbranded>(() =>
    withoutHidden(
      schema,
      form,
      withRequiredGroups(schema, initialDocument),
      context,
    ),
  );
  // The document as of the latest answer, which two answers given before a
  // re-render must each build on.
  const latest = useRef(document);
  const [submitted, setSubmitted] = useState(false);

  const hidden = hiddenIn(schema, form, document, context);
  // Errs only for a schema no validator can be built from, as for `hiddenIn`,
  // so every banner is drawn rather than any silently dropped.
  const shown = withBannersShown({ schema, form, document, context }).unwrapOr(
    form,
  );
  // Errs only for a schema no validator can be built from, as for `hiddenIn`,
  // so no comment is marked required; the problems already refuse the form as
  // `cannot-check`.
  const commentsDue = commentsRequired({
    schema,
    form,
    document,
    context,
  }).unwrapOr(new Set<string>());
  const commentLabelOf = commentLabelFor(t, language);
  const presented = presentationOf({
    schema,
    form,
    document,
    context,
    source,
    requestedLanguage: language,
    commentLabelOf,
  });
  const problems = problemsIn({ schema, form, document, context })
    .andThen((found) => presented.map(() => found))
    .match(
      (found) => found,
      (error): FormProblem[] => [
        { kind: "answer", at: "", code: "cannot-check", detail: error.message },
      ],
    );
  const presentation = presented.unwrapOr(undefined);
  const accepted = problems.length === 0 && presentation !== undefined;
  // A problem is a code; the words for it are this collector's, in the
  // respondent's language.
  const wordsFor = (problem: FormProblem) =>
    t(`assessment.form.problems.${problem.code}`, {
      ...problem.params,
      defaultValue: problem.code,
    });

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
      <Box
        component="form"
        // The instrument and its checks judge the document; `required` only
        // marks the labels.
        noValidate
        sx={{ flex: 1 }}
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);

          if (accepted) {
            void onSubmit({ document, presentation });
          }
        }}
      >
        {/* Disabled while a submission is on its way, so no answer changes
            after the document sent was taken. */}
        <Box
          component="fieldset"
          disabled={submitting}
          sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
        >
          <Stack spacing={3}>
            {shown.map((element) => {
              if (element.kind === "group") {
                return hidden.groups.has(element.at.group) ? null : (
                  <Box key={`group ${element.at.group}`}>
                    <Typography variant="h6" component="h2">
                      {element.asking.required === "by_instrument"
                        ? element.presentation.title.text
                        : t("assessment.form.optionalGroup", {
                            title: element.presentation.title.text,
                          })}
                    </Typography>
                    <Description
                      text={element.presentation.description?.text}
                    />
                  </Box>
                );
              }

              return hidden.items.has(element.at.item) ? null : (
                <ItemField
                  key={`item ${element.at.group} ${element.at.item}`}
                  element={element}
                  commentLabel={commentLabelOf(element).text}
                  commentRequired={commentsDue.has(element.at.item)}
                  answer={answerOf(document, element)}
                  problems={
                    submitted ? problemsAt(problems, element).map(wordsFor) : []
                  }
                  commentProblems={
                    submitted
                      ? commentProblemsAt(problems, element).map(wordsFor)
                      : []
                  }
                  onAnswer={(answer) => {
                    const next = answered(
                      { schema, form, context },
                      latest.current,
                      element,
                      answer,
                    );

                    latest.current = next;
                    setDocument(next);
                    void onDocumentChange?.(next);
                  }}
                />
              );
            })}
            <Box>
              <Button type="submit" variant="contained" disabled={submitting}>
                {t("assessment.form.submit")}
              </Button>
            </Box>
            {footer}
          </Stack>
        </Box>
      </Box>
      {showWorking ? (
        <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
          {aside}
          <Box component="section" aria-labelledby="assessment-form-document">
            <Typography
              id="assessment-form-document"
              variant="subtitle1"
              component="h2"
            >
              {t("assessment.form.document")}
            </Typography>
            <Box component="pre" sx={{ fontSize: 13, overflowX: "auto" }}>
              {JSON.stringify(document, null, 2)}
            </Box>
          </Box>
          {accepted ? (
            <Alert severity="success">{t("assessment.form.passes")}</Alert>
          ) : (
            <Alert severity="warning">
              {t("assessment.form.refuses")}
              <ul>
                {problems.map((problem) => (
                  <li key={`${problem.at} ${problem.code}`}>
                    {problem.at}: {wordsFor(problem)}
                    {problem.detail === undefined ? null : (
                      <Typography variant="caption" component="div">
                        {problem.detail}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          {presentation === undefined ? null : (
            <Box component="section" aria-labelledby="assessment-form-evidence">
              <Typography
                id="assessment-form-evidence"
                variant="subtitle1"
                component="h2"
              >
                {t("assessment.form.evidence")}
              </Typography>
              <Box component="pre" sx={{ fontSize: 13, overflowX: "auto" }}>
                {JSON.stringify(presentation, null, 2)}
              </Box>
            </Box>
          )}
        </Paper>
      ) : null}
    </Stack>
  );
};
